import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
export function ContactUs() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-916">
      <SEOHead title="Contact Us | Smith & Jengs Cleaning Services" description="Get in touch with Smith & Jengs Cleaning Services in Bicester, Oxfordshire. Request a callback, ask questions, or book a cleaning. Call +447432285564 or email smithsandjengs@yahoo.com." canonical="https://smithandjengs.com/contact-us" data-id="element-917" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Contact Us',
      url: 'https://smithandjengs.com/contact-us'
    }]} data-id="element-918" />
      <Navbar data-id="element-919" />

      <main className="flex-1" data-id="element-920">
        {/* 1. Hero Banner */}
        <section className="relative w-full h-[55vh] min-h-[420px] max-h-[600px] overflow-hidden bg-[#1a2744]" data-id="element-921">
          <div className="absolute inset-0" data-id="element-922">
            <img src="https://cdn.magicpatterns.com/uploads/6QKxnCfN2Sqn8DtA2jU5tB/sj-website-contact-us-banner.png" alt="Cleaner holding cleaning supplies" className="w-full h-full object-cover object-right-top" decoding="async" data-id="element-923" />
            {/* Dark gradient overlay on left/bottom for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2744]/90 via-[#1a2744]/50 to-transparent" data-id="element-924" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2744]/70 via-transparent to-transparent" data-id="element-925" />
          </div>

          <div className="relative z-10 h-full flex items-end px-6 md:px-12 pb-12 pt-32" data-id="element-926">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="max-w-7xl mx-auto w-full" data-id="element-927">
              <h1 className="font-bebas text-white leading-[0.85] tracking-wide" data-id="element-928">
                <span className="text-7xl md:text-[9rem] lg:text-[11rem] block" data-id="element-929">
                  CONTACT
                </span>
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-7xl md:text-[8rem] lg:text-[10rem] block -mt-2 md:-mt-6" data-id="element-930">
                  Us
                </span>
              </h1>
            </motion.div>
          </div>
        </section>

        {/* 2. Request Callback Form Section */}
        <section className="w-full bg-white" data-id="element-931">
          <div className="flex flex-col lg:flex-row w-full min-h-[800px]" data-id="element-932">
            {/* Left: Image with Yellow Background */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full lg:w-[45%] bg-[#f5c518] relative min-h-[400px] lg:min-h-full flex items-center justify-center overflow-hidden" data-id="element-933">
              <img src="https://cdn.magicpatterns.com/uploads/e78W1heVEAoUdPavobxBNo/sj-website-contact-us-img1.png" alt="Pink cleaning glove holding orange sponge" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-934" />
            </motion.div>

            {/* Right: Form */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full lg:w-[55%] px-6 py-16 md:px-16 lg:py-24 flex flex-col justify-center" data-id="element-935">
              <div className="max-w-2xl w-full mx-auto lg:mx-0" data-id="element-936">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-12" data-id="element-937">
                  <h2 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide" data-id="element-938">
                    <span className="text-6xl md:text-[5.5rem] lg:text-[6.5rem] block" data-id="element-939">
                      REQUEST
                    </span>
                    <span className="text-6xl md:text-[5.5rem] lg:text-[6.5rem] block" data-id="element-940">
                      CALLBACK
                    </span>
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 font-medium max-w-xs text-left md:text-right mt-2" data-id="element-941">
                    We're happy to answer any questions you have and provide you
                    with more information. Just send us a message and we'll get
                    back to you as soon as we can!
                  </p>
                </div>

                <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-942">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-943">
                    <div className="relative" data-id="element-944">
                      <input type="text" id="firstName" placeholder="First Name" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-945" />
                    </div>
                    <div className="relative" data-id="element-946">
                      <input type="text" id="lastName" placeholder="Last Name" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-947" />
                    </div>
                  </div>

                  <div className="relative" data-id="element-948">
                    <input type="email" id="email" placeholder="Email Address" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-949" />
                  </div>

                  <div className="relative" data-id="element-950">
                    <input type="tel" id="phone" placeholder="Phone Number" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-951" />
                  </div>

                  <div className="relative" data-id="element-952">
                    <input type="text" id="postalCode" placeholder="Postal Code" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-953" />
                  </div>

                  <div className="relative" data-id="element-954">
                    <input type="text" id="subject" placeholder="Subject" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-955" />
                  </div>

                  <div className="relative pt-4" data-id="element-956">
                    <label htmlFor="message" className="block text-sm text-gray-500 mb-2" data-id="element-957">
                      Additional Information
                    </label>
                    <textarea id="message" rows={4} className="w-full border border-gray-300 rounded-sm p-4 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all resize-none" data-id="element-958"></textarea>
                  </div>

                  <div className="flex justify-end pt-4" data-id="element-959">
                    <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-10 py-3 rounded-full font-semibold tracking-wider text-sm transition-colors shadow-md" data-id="element-960">
                      SEND
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 3. Contact Info + Map Section */}
        <section className="w-full bg-[#f8f9fa] py-16 md:py-24 px-6 md:px-12" data-id="element-961">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-962">
            {/* Left: Contact Info */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="flex flex-col" data-id="element-963">
              <div className="mb-10" data-id="element-964">
                <img src="https://cdn.magicpatterns.com/uploads/iQahCaoqLWtvpaEqqgu2am/sj-website-sj-text-stacked-colour.svg" alt="Smith & Jengs" className="w-48 md:w-56 h-auto mb-2" decoding="async" data-id="element-965" />
                <p className="text-[#1a2744] font-semibold tracking-[0.15em] text-sm ml-1" data-id="element-966">
                  CLEANING SERVICES
                </p>
              </div>

              <div className="space-y-8 text-[#1a2744]" data-id="element-967">
                <div className="flex items-start gap-4" data-id="element-968">
                  <MapPinIcon className="w-5 h-5 mt-1 text-gray-400 flex-shrink-0" data-id="element-969" />
                  <div data-id="element-970">
                    <p className="text-lg font-medium" data-id="element-971">Bicester</p>
                    <p className="text-lg font-medium" data-id="element-972">United Kingdom</p>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-973">
                  <div className="flex items-center gap-4" data-id="element-974">
                    <PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-975" />
                    <a href="tel:+447432285564" className="text-base hover:text-[#e91e8c] transition-colors" data-id="element-976">
                      +447432285564
                    </a>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-977">
                    <PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-978" />
                    <a href="tel:01869601439" className="text-base hover:text-[#e91e8c] transition-colors" data-id="element-979">
                      01869601439
                    </a>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-980">
                    <MailIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-981" />
                    <a href="mailto:smithsandjengs@yahoo.com" className="text-base hover:text-[#e91e8c] transition-colors" data-id="element-982">
                      smithsandjengs@yahoo.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Map */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden shadow-lg border border-gray-200" data-id="element-983">
              <img src="https://cdn.magicpatterns.com/uploads/b6BxcUSupiMq6jBRvxQfCM/sj-website-contact-us-map.png" alt="Map showing Smith and Jengs service area in Bicester, Oxfordshire" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-984" />
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-985" />
    </div>;
}